import React, { useState, useEffect, useCallback } from "react";
import { FaPhone, FaMapMarkerAlt, FaLinkedin, FaTwitter, FaFacebook, FaChevronLeft, FaChevronRight, FaQuoteRight } from "react-icons/fa";
import { MdOutlineAlternateEmail } from "react-icons/md";

const TestimonialSlider = () => {
  const testimonials = [
    {
      id: 1,
      quote: "RxUltimate has transformed our pharmacy operations completely. The system is intuitive, comprehensive, and has helped us reduce errors by 75% while improving customer satisfaction.",
      name: "Sarah Johnson",
      position: "Pharmacy Director, HealthPlus",
      avatar: "1"
    },
    {
      id: 2,
      quote: "The inventory management feature alone saved us thousands in the first month. We're now able to forecast our needs accurately and prevent stockouts while reducing overstock.",
      name: "Michael Chen",
      position: "Owner, Community Care Pharmacy",
      avatar: "2"
    },
    {
      id: 3,
      quote: "Our patients love the digital prescription refill system. It's cut down wait times dramatically and the automatic reminders have increased our customer retention by 40%.",
      name: "Amara Okafor",
      position: "Lead Pharmacist, MediPlus",
      avatar: "3"
    },
    {
      id: 4,
      quote: "The customer support team at RxUltimate deserves special mention. They've been incredibly responsive and have customized the system to meet our unique workflow needs.",
      name: "David Rodriguez",
      position: "Operations Manager, PharmaCare Group",
      avatar: "4"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoplay, setAutoplay] = useState(true);

  const nextSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [isAnimating, testimonials.length]);

  const prevSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [isAnimating, testimonials.length]);

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay, nextSlide]);

  return (
    <div 
      className="relative" 
      onMouseEnter={() => setAutoplay(false)}
      onMouseLeave={() => setAutoplay(true)}
    >
      <h3 className="text-2xl font-bold text-center mb-8">What Our Clients Say</h3>
      
      <div className="relative h-64">
        {testimonials.map((testimonial, index) => (
          <div
            key={testimonial.id}
            className={`absolute top-0 left-0 w-full transition-all duration-500 ease-in-out ${
              index === currentIndex 
                ? "opacity-100 translate-x-0" 
                : index < currentIndex 
                  ? "opacity-0 -translate-x-full" 
                  : "opacity-0 translate-x-full"
            }`}
          >
            <div className="flex justify-center mb-6">
              <div className="bg-blue-100 rounded-full p-3">
                <FaQuoteRight className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <p className="text-lg text-gray-700 text-center italic mb-6 px-8">
              "{testimonial.quote}"
            </p>
            <div className="flex justify-center items-center">
              <div className="h-12 w-12 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-lg mr-4">
                {testimonial.avatar[0]}
              </div>
              <div>
                <h4 className="font-bold text-gray-800">{testimonial.name}</h4>
                <p className="text-blue-600">{testimonial.position}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 bg-white bg-opacity-50 hover:bg-opacity-100 rounded-full p-2 shadow-md text-blue-700 transition-all duration-300 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
        aria-label="Previous testimonial"
      >
        <FaChevronLeft className="h-5 w-5" />
      </button>
      
      <button 
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white bg-opacity-50 hover:bg-opacity-100 rounded-full p-2 shadow-md text-blue-700 transition-all duration-300 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
        aria-label="Next testimonial"
      >
        <FaChevronRight className="h-5 w-5" />
      </button>
      
      {/* Dot Indicators */}
      <div className="flex justify-center mt-8 space-x-2">
        {testimonials.map((_, index) => (
          <button 
            key={index}
            onClick={() => {
              if (!isAnimating) {
                setIsAnimating(true);
                setCurrentIndex(index);
                setTimeout(() => setIsAnimating(false), 500);
              }
            }}
            className={`h-2.5 rounded-full transition-all duration-300 focus:outline-none ${
              index === currentIndex ? "w-8 bg-blue-600" : "w-2.5 bg-gray-300 hover:bg-blue-300"
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const JoinUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  // Animation for section visibility
  useEffect(() => {
    setIsVisible(true);
    
    // Reset form after submission feedback
    if (submitted) {
      const timer = setTimeout(() => {
        setSubmitted(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setFormData({ name: "", email: "", message: "" });
    setSubmitted(true);
  };

  return (
    <div className={`py-16 border-t border-blue-500 bg-gradient-to-b from-white to-blue-50 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} id="join-us">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-16 transform transition-all duration-700 ease-in-out">
          <p className="text-sm text-blue-600 bg-blue-50 border border-blue-200 shadow-sm rounded-full py-2 px-6 w-max mx-auto mb-4 font-medium">
            Join Our Network
          </p>
          <h2 className="md:text-5xl text-3xl text-center font-bold mb-6 lg:w-3/4 mx-auto transition-all duration-500">
            We guarantee customer satisfaction through{" "}
            <span className="text-blue-600 relative inline-block">
              Professional
              <span className="absolute bottom-0 left-0 w-full h-1 bg-blue-400 rounded-full"></span>
            </span>{" "}
            Care
          </h2>
          <p className="text-gray-600 text-center text-lg mb-10 lg:w-2/3 mx-auto">
            Experience the future of pharmacy management with RxUltimate. 
            Our cutting-edge solution streamlines operations, enhances patient care, 
            and boosts your pharmacy's efficiency.
          </p>
        </div>

        {/* Contact Cards and Form Grid */}
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Contact Information Cards */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl flex items-center gap-5 bg-white p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1">
              <div className="bg-blue-600 p-4 rounded-lg">
                <FaPhone className="text-white text-xl" />
              </div>
              <div>
                <h4 className="text-gray-800 font-medium text-xl">Phone Contact</h4>
                <p className="text-blue-600 font-medium">(+233) 54154879654</p>
              </div>
            </div>
            
            <div className="rounded-xl flex items-center gap-5 bg-white p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1">
              <div className="bg-blue-600 p-4 rounded-lg">
                <MdOutlineAlternateEmail className="text-white text-xl" />
              </div>
              <div>
                <h4 className="text-gray-800 font-medium text-xl">Email Address</h4>
                <p className="text-blue-600 font-medium">info@rxultimate.com</p>
              </div>
            </div>
            
            <div className="rounded-xl flex items-center gap-5 bg-white p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1">
              <div className="bg-blue-600 p-4 rounded-lg">
                <FaMapMarkerAlt className="text-white text-xl" />
              </div>
              <div>
                <h4 className="text-gray-800 font-medium text-xl">Office Location</h4>
                <p className="text-blue-600 font-medium">Accra, Ghana</p>
              </div>
            </div>
            
            {/* Social Media Links */}
            <div className="flex justify-center gap-4 mt-8">
              <a href="#" className="bg-white p-3 rounded-full shadow-md hover:bg-blue-100 transition-colors duration-300">
                <FaLinkedin className="text-blue-600 text-xl" />
              </a>
              <a href="#" className="bg-white p-3 rounded-full shadow-md hover:bg-blue-100 transition-colors duration-300">
                <FaTwitter className="text-blue-600 text-xl" />
              </a>
              <a href="#" className="bg-white p-3 rounded-full shadow-md hover:bg-blue-100 transition-colors duration-300">
                <FaFacebook className="text-blue-600 text-xl" />
              </a>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-500">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h3>
            
            {submitted ? (
              <div className="bg-blue-100 border border-blue-200 text-blue-700 p-4 rounded-lg mb-4 animate-pulse">
                Thank you for reaching out! We'll be in touch shortly.
              </div>
            ) : null}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Full Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email Address</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Your Message</label>
                <textarea 
                  id="message" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4" 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                  placeholder="I'm interested in learning more about RxUltimate..."
                  required
                ></textarea>
              </div>
              
              <div className="text-center">
                <button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  Schedule a Demo
                </button>
              </div>
            </form>
            
            <div className="mt-8 text-center text-gray-500 text-sm">
              <p>By submitting this form, you agree to our <a href="#" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>.</p>
            </div>
          </div>
        </div>
        
        {/* Testimonial Section - Slider */}
        <div className="mt-20 bg-white rounded-2xl shadow-lg p-8 lg:w-3/4 mx-auto relative overflow-hidden">
          <TestimonialSlider />
        </div>
      </div>
    </div>
  );
};

export default JoinUs;